<template>
  <div class="page-wrapper">
    <div class="container">

      <!-- Page Header - Using PageHero component -->
      <PageHero title="Support Organlive" />

      <!-- Support Button -->
      <section class="content-section support-button-section">
        <a
          href="https://organ.media/support"
          target="_blank"
          rel="noopener"
          class="support-button"
        >
          Support Organlive Today
        </a>
      </section>

      <!-- Introduction -->
      <section class="content-section">
        <p>
          For more than twenty years, Organlive has existed because listeners like you believe in the importance of organ music. Every hour we broadcast, every new album added to our library, every improvement made to our service — it all happens because our audience has chosen to keep this unique station alive.
        </p>
        <p>
          Unlike commercial broadcasters, Organlive receives no government funding, no corporate underwriting, and no advertising revenue. We rely entirely on the generosity of our listeners to continue streaming the world's organ music 24 hours a day.
        </p>
      </section>

      <!-- What Support Covers -->
      <section class="content-section">
        <h2>Your support helps cover the essential costs that keep Organlive on the air:</h2>

        <div class="cost-item">
          <h3>• Domain, hosting, and server infrastructure</h3>
          <p>Reliable servers and storage ensure that our stream, website, and growing album library remain accessible around the clock.</p>
        </div>

        <div class="cost-item">
          <h3>• Music licensing fees</h3>
          <p>This is our largest ongoing expense. Even as a small, niche broadcaster, we are required to pay the same rates to ASCAP, SESAC, BMI, and SoundExchange as much larger stations with far greater budgets. These fees are important — they ensure that the organists, ensembles, and recording labels whose music we play are fairly compensated for their work.</p>
        </div>

        <div class="cost-item">
          <h3>• Expanding and maintaining our music library</h3>
          <p>New albums, historical recordings, and listener submissions all require time, storage, and cataloging.</p>
        </div>
      </section>

      <!-- Why Your Contribution Matters -->
      <section class="content-section">
        <h2>Why Your Contribution Matters</h2>
        <p>
          Organlive is more than a radio station — it is a worldwide community of people who love the organ and want to keep its music alive, accessible, and thriving. Your donation directly supports that mission.
        </p>
        <p>By giving today, you help ensure:</p>
        <ul class="checkmark-list">
          <li>✔ that the organ continues to have a dedicated broadcast platform</li>
          <li>✔ that artists are properly supported through licensing royalties</li>
          <li>✔ that future listeners can enjoy the richness of this repertoire for years to come</li>
        </ul>
        <p class="tax-info">
          Organlive is produced by the Organ Media Foundation, a 501(c)(3) nonprofit organization. All donations are tax-deductible where allowed by law.
        </p>
      </section>

      <!-- Thank You -->
      <section class="content-section">
        <h2>Thank You</h2>
        <p>
          Whether you give once or choose to support us monthly, your generosity makes a real difference. Every contribution — large or small — helps us continue sharing the world's most magnificent musical instrument with audiences everywhere.
        </p>
      </section>

    </div>
  </div>
</template>

<script>
import PageHero from '@/components/PageHero.vue'

export default {
  name: 'SupportPage',
  components: {
    PageHero
  },
  mounted () {
    document.title = 'Organlive.com - Support Organlive'
  }
}
</script>

<style scoped>
/* Page-specific styles only - layout and hero are now global */

/* Support Button Section */
.support-button-section {
  text-align: center;
  padding: 2rem;
  background: var(--bg-primary-light);
  border: 2px solid var(--color-primary);
}

.support-button {
  display: inline-block;
  background: var(--btn-gradient);
  color: var(--color-white);
  font-size: 1.8rem;
  font-weight: bold;
  padding: 1.5rem 3rem;
  border-radius: var(--radius-default);
  text-decoration: none;
  transition: all var(--transition-default);
  box-shadow: var(--shadow-button);
}

.support-button:hover {
  background: var(--btn-gradient-hover);
  transform: translateY(-2px);
  box-shadow: var(--shadow-button-hover);
}

/* Cost Items */
.cost-item {
  margin: 1.5rem 0;
}

.cost-item h3 {
  color: var(--color-primary);
  font-size: 1.2rem;
  margin: 0 0 0.5rem;
  font-weight: 600;
}

.cost-item p {
  margin: 0;
  padding-left: 1.5rem;
}

/* Checkmark List */
.checkmark-list {
  list-style: none;
  padding: 0;
  margin: 1rem 0;
}

.checkmark-list li {
  color: var(--color-white);
  font-size: var(--font-size-body);
  line-height: var(--line-height-normal);
  padding: 0.5rem 0;
}

/* Tax Info */
.tax-info {
  font-style: italic;
  color: var(--color-gray);
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

/* Responsive Design */
@media (max-width: 768px) {
  .support-button {
    font-size: 1.4rem;
    padding: 1.2rem 2rem;
  }

  .cost-item h3 {
    font-size: 1.1rem;
  }

  .cost-item p {
    padding-left: 1rem;
    font-size: 1rem;
  }

  .checkmark-list li {
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .support-button {
    font-size: 1.2rem;
    padding: 1rem 1.5rem;
    width: 100%;
  }

  .cost-item h3 {
    font-size: 1rem;
  }

  .cost-item p {
    font-size: 0.95rem;
  }

  .checkmark-list li {
    font-size: 0.95rem;
  }
}
</style>
